// src/components/OverlayDetails.js
import React, { useState, useEffect } from "react";
import { httpClient } from '../../../../HttpClient';
import {
  Building2,
  Users,
  Briefcase,
  CalendarCheck,
  FileText,
  HelpCircle,
  ClipboardCheck,
} from "lucide-react";

const OverlayDetails = ({
  overlayData,
  formatDateWithoutYear,
  selectedYear,
}) => {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    httpClient
      .get("https://backend-myalfa.vercel.app/api/users")
      .then((res) => setUsers(res.data))
      .catch((err) => console.error(err));
  }, []);

  if (!overlayData) return null;

  const Checkbool = (value) => (
    <div className="flex items-center gap-2">
      <div
        className={`w-4 h-4 rounded-full border flex items-center justify-center
          ${value ? "border-emerald-600" : "border-slate-400"}
        `}
      >
        {value && <div className="w-2 h-2 rounded-full bg-emerald-600" />}
      </div>
    </div>
  );

  const CheckboolS = Checkbool;

  const Tooltip = ({ children, content }) => {
    const [visible, setVisible] = useState(false);

    return (
      <span
        style={{ position: "relative", cursor: "pointer" }}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
      >
        {children}
        {visible && <div>{content}</div>}
      </span>
    );
  };

  const getUserNameByEmail = (email) => {
    if (!email) return "";

    const user = users.find((u) => u.email === email);

    // 🟢 Cas 1 : utilisateur trouvé et infos complètes
    if (user?.name && user?.lastName) {
      return `${user.lastName} ${user.name}`;
    }

    // 🟡 Cas 2 : fallback → on découpe l'email
    const emailPart = email.split("@")[0]; // lastName.name
    if (!emailPart.includes(".")) return email;

    const [lastName, name] = emailPart.split(".");

    const format = (str) =>
      str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

    return `${format(lastName)} ${format(name)}`;
  };

  const DeclarationRow = ({ label, check, date, ar }) => (
    <div className="grid grid-cols-5 items-center text-xs py-2 border-b last:border-b-0">
      <span className="text-slate-500">{label}</span>
      {Checkbool(check)}
      <span className="text-slate-900">
        {formatDateWithoutYear(date) || "-"}
      </span>
      <span className="text-slate-500">A/R</span>
      {Checkbool(ar)}
    </div>
  );

  return (
    <div className="w-full py-6 px-10">
      <div className="text-xs text-slate-600">
        <span className="font-semibold">DOSSIER ANNUEL</span>
        <span className="mx-2">•</span>
        <span>Révision de {selectedYear}</span>
      </div>
      <h2 className="text-xl font-bold text-slate-900 mt-2">
        {overlayData.client.nom}
      </h2>

      <div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white border border-slate-200 rounded-lg p-3 mt-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded bg-pink-100 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-pink-600" />
              </div>
              <h3 className="font-semibold text-sm text-slate-900 m-0">
                Information client
              </h3>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Forme Juridique</span>
                <span className="font-medium text-slate-900">
                  {overlayData.client.formeSociale || "-"}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Régime Fiscal</span>
                <span className="font-medium text-slate-900">
                  {overlayData.client.regimeFiscal || "-"}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Régime TVA</span>
                <span className="font-medium text-slate-900">
                  {overlayData.client.regimeTVA || "-"}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Collaborateur</span>
                <span className="font-medium text-slate-900">
                  {overlayData.client.collaborateur || "-"}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Date d'échéance</span>
                <span className="font-medium text-slate-900">
                  {overlayData.suivi.dueDate
                    ? new Date(overlayData.suivi.dueDate).toLocaleDateString(
                        "fr-FR"
                      )
                    : "-"}
                </span>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-3 mt-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded bg-pink-100 flex items-center justify-center">
                <Users className="w-4 h-4 text-pink-600" />
              </div>
              <h3 className="font-semibold text-sm text-slate-900 m-0">
                Équipe Cabinet
              </h3>
            </div>
            <div className="space-y-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-full bg-pink-600 flex items-center justify-center">
                    <span className="text-white text-xs font-semibold">
                      {overlayData.client.email_expertComptable
                        ?.split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase() || "EC"}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-900">
                      Expert comptable
                    </p>
                    <p className="text-xs text-slate-600">
                      {getUserNameByEmail(
                        overlayData.client.email_expertComptable || "-"
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <div className="border-t pt-2">
                <p className="text-[10px] text-slate-500 uppercase mb-1">
                  Collaborateur Niv. 1
                </p>
                <p className="text-xs text-slate-900">
                  {getUserNameByEmail(
                    overlayData.client.email_collaborateur || "-"
                  )}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 uppercase mb-1">
                  {getUserNameByEmail(
                    overlayData.client.email_collaborateur_niv2 || "-"
                  )}
                </p>
                <p className="text-xs text-slate-900">-</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Conformité LCB/FT ---- */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded bg-pink-100 flex items-center justify-center">
            <Briefcase className="w-4 h-4 text-pink-600" />
          </div>
          <h3 className="font-semibold text-sm text-slate-900">
            Conformité LCB/FT
          </h3>
        </div>

        <div className="flex flex-col gap-4 text-xs">
          {/* Kanta */}
          <div className="flex justify-between gap-1">
            <span className="text-slate-500">Mise à jour Kanta</span>
            <p> {Checkbool(overlayData.suivi.kanta)} </p>
          </div>

          {/* Lettre de mission */}
          <div className="flex justify-between gap-1">
            <span className="text-slate-500">Lettre de mission</span>
            <span className="font-medium text-slate-900">
              {formatDateWithoutYear(overlayData.suivi.LettreMisson) || "-"}
            </span>
          </div>

          {/* Questionnaire LAB */}
          <div className="flex justify-between gap-1">
            <span className="text-slate-500">Questionnaire LAB mis à jour</span>
            <p> {Checkbool(overlayData.suivi.LAB)} </p>
          </div>

          {/* Maintien mission */}
          <div className="flex justify-between gap-1">
            <span className="text-slate-500">
              Formulaire maintien de la mission
            </span>
            <p> {Checkbool(overlayData.suivi.maintientMission)} </p>
          </div>

          {/* Fiche revue */}
          <div className="flex justify-between gap-1">
            <span className="text-slate-500">
              Fiche de revue annuelle / Examen d’opérations
            </span>
            <p> {Checkbool(overlayData.suivi.ficheRevue)} </p>
          </div>
        </div>
      </div>

      {/* ---- RDV Bilan ---- */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded bg-pink-100 flex items-center justify-center">
            <CalendarCheck className="w-4 h-4 text-pink-600" />
          </div>
          <h3 className="font-semibold text-sm text-slate-900">RDV Bilan</h3>
        </div>

        <div className="flex justify-between text-xs">
          <span className="text-slate-500">Date du rendez-vous</span>
          <span className="font-medium text-slate-900">
            {formatDateWithoutYear(overlayData.suivi.rdvClient) || "-"}
          </span>
        </div>
      </div>

      {/* ---- Déclarations fiscales ---- */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded bg-pink-100 flex items-center justify-center">
            <FileText className="w-4 h-4 text-pink-600" />
          </div>
          <h3 className="font-semibold text-sm text-slate-900">
            Déclarations fiscales
          </h3>
        </div>

        <div className="flex flex-col gap-1">
          <DeclarationRow
            label="3517-SD"
            check={overlayData.suivi.s3517check}
            date={overlayData.suivi.s3517}
            ar={overlayData.suivi.s3517AR}
          />
          <DeclarationRow
            label="2572-SD"
            check={overlayData.suivi.s2572check}
            date={overlayData.suivi.s2572}
            ar={overlayData.suivi.s2572AR}
          />
          <DeclarationRow
            label="3573-SD"
            check={overlayData.suivi.s3573check}
            date={overlayData.suivi.s3573}
            ar={overlayData.suivi.s3573AR}
          />
          <DeclarationRow
            label="1329-DEF"
            check={overlayData.suivi.def1329check}
            date={overlayData.suivi.def1329}
            ar={overlayData.suivi.def1329AR}
          />
          <DeclarationRow
            label="TDFC"
            check={overlayData.suivi.tdfccheck}
            date={overlayData.suivi.tdfc}
            ar={overlayData.suivi.tdfcAR}
          />
        </div>

        {/* Autres déclarations */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 text-xs">
          {[
            ["Liasse fiscale", overlayData.suivi.liasseFiscale],
            ["1330-CVAE", overlayData.suivi.cvae1330],
            ["2082-SD", overlayData.suivi.s2082],
            ["2083-SD", overlayData.suivi.s2083],
            ["Art. 44-sexies", overlayData.suivi.sexies44],
            ["2069-RCI", overlayData.suivi.rci2069],
            ["2079", overlayData.suivi.s2079],
            ["DEC-LOYER", overlayData.suivi.decLoyer],
            ["Tableaux OG", overlayData.suivi.oga],
          ].map(([label, value]) => (
            <div key={label} className="flex justify-between items-center">
              <span className="text-slate-500">{label}</span>
              {Checkbool(value)}
            </div>
          ))}
        </div>
      </div>

      {/* ---- Etats financiers - Annexe ---- */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded bg-pink-100 flex items-center justify-center">
            <FileText className="w-4 h-4 text-pink-600" />
          </div>
          <h2 className="font-semibold text-slate-900 text-sm m-0">
            États financiers - Annexe
          </h2>
        </div>

        {[
          [
            "microEntreprise",
            "Dispense d'annexe micro-entreprise (hors holding)",
            <>
              <div className="grid grid-cols-[auto_auto] gap-x-2">
                <p>✓ Total du bilan</p>
                <p>≤ 350 000 €</p>
              </div>
              <div className="grid grid-cols-[auto_auto] gap-x-2">
                <p>✓ CA net</p>
                <p>≤ 700 000 €</p>
              </div>
              <div className="grid grid-cols-[auto_auto_1fr] gap-x-2 items-center text-xs">
                <p>✓ Nombre moyen de salariés</p>
                <p>≤ 10</p>
                <p className="text-[0.8rem]">
                  (moyenne à la fin de chaque trimestre de l'année civile, ou de
                  l'exercice si différent)
                </p>
              </div>
            </>,
          ],
          [
            "personnePhysique",
            "Dispense d'annexe personne physique",
            <>
              <div>✓ Personne physique</div>
              <div>✓ Régime fiscal : RSI</div>
            </>,
          ],
          [
            "petiteEntreprise",
            "Présentation simplifiée petite-entreprise",
            <>
              <div className="grid grid-cols-[auto_auto] gap-x-2">
                <p>✓ Total du bilan</p>
                <p>≤ 4 000 000 €</p>
              </div>
              <div className="grid grid-cols-[auto_auto] gap-x-2">
                <p>✓ CA net</p>
                <p>≤ 8 000 000 €</p>
              </div>
              <div className="grid grid-cols-[auto_auto] gap-x-2">
                <p>✓ Nombre moyen de salariés</p>
                <p>≤ 50</p>
              </div>
            </>,
          ],
          [
            "abregee",
            "Présentation annexe abrégée",
            <>
              <div>✓ Personne morale</div>
              <div>✓ Régime fiscal : RSI</div>
            </>,
          ],
        ].map(([key, label, tooltipContent]) => (
          <div key={key} className="flex items-center gap-2 mt-2 text-xs">
            {Checkbool(overlayData.suivi[key])}
            <span className="font-medium text-slate-900">{label}</span>
            <Tooltip content={tooltipContent}>
              <HelpCircle className="w-4 h-4 text-slate-400 cursor-pointer" />
            </Tooltip>
          </div>
        ))}
      </div>

      {/* ---- Nomination du CAC ---- */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded bg-pink-100 flex items-center justify-center">
            <Users className="w-4 h-4 text-pink-600" />
          </div>
          <h2 className="font-semibold text-slate-900 text-sm">
            Nomination du CAC
          </h2>
        </div>

        <div className="flex flex-col gap-2 text-xs">
          {/* NA */}
          <div className="flex items-center gap-2">
            {Checkbool(overlayData.suivi.nA)}
            <span className="font-medium text-slate-900">N/A</span>
          </div>

          {/* Cas général */}
          <div className="flex items-center gap-2">
            {Checkbool(overlayData.suivi.casGeneral)}
            <span className="font-medium text-slate-900">
              Cas général (y compris "Petit groupe / Société mère")
            </span>
            <Tooltip
              content={
                <div className="p-2 bg-white border border-slate-200 rounded shadow text-xs space-y-1">
                  <div className="grid grid-cols-[auto_auto] gap-x-2">
                    <p>✓ Total du bilan</p>
                    <p>≤ 4 000 000 €</p>
                  </div>
                  <div className="grid grid-cols-[auto_auto] gap-x-2">
                    <p>✓ CA net</p>
                    <p>≤ 8 000 000 €</p>
                  </div>
                  <div className="grid grid-cols-[auto_auto] gap-x-2">
                    <p>✓ Nombre moyen de salariés</p>
                    <p>≤ 50</p>
                  </div>
                </div>
              }
            >
              <HelpCircle className="w-4 h-4 text-slate-400 cursor-pointer" />
            </Tooltip>
          </div>

          {/* Organisme de formation */}
          <div className="flex items-center gap-2">
            {Checkbool(overlayData.suivi.organismeFormation)}
            <span className="font-medium text-slate-900">
              Organisme de formation
            </span>
            <Tooltip
              content={
                <div className="p-2 bg-white border border-slate-200 rounded shadow text-xs space-y-1">
                  <div className="grid grid-cols-[auto_auto] gap-x-2">
                    <p>✓ Total du bilan</p>
                    <p>≤ 230 000 €</p>
                  </div>
                  <div className="grid grid-cols-[auto_auto] gap-x-2">
                    <p>✓ CA net</p>
                    <p>≤ 153 000 €</p>
                  </div>
                  <div className="grid grid-cols-[auto_auto] gap-x-2">
                    <p>✓ Nombre moyen de salariés</p>
                    <p>≤ 3</p>
                  </div>
                </div>
              }
            >
              <HelpCircle className="w-4 h-4 text-slate-400 cursor-pointer" />
            </Tooltip>
          </div>

          {/* Petit groupe / Sociétés contrôlées */}
          <div className="flex items-center gap-2">
            {Checkbool(overlayData.suivi.petitGroupe)}
            <span className="font-medium text-slate-900">
              "Petit groupe / Sociétés contrôlées"
            </span>
            <Tooltip
              content={
                <div className="p-2 bg-white border border-slate-200 rounded shadow text-xs space-y-1">
                  <div className="grid grid-cols-[auto_auto] gap-x-2">
                    <p>✓ Total du bilan</p>
                    <p>≤ 2 000 000 €</p>
                  </div>
                  <div className="grid grid-cols-[auto_auto] gap-x-2">
                    <p>✓ CA net</p>
                    <p>≤ 4 000 000 €</p>
                  </div>
                  <div className="grid grid-cols-[auto_auto] gap-x-2">
                    <p>✓ Nombre moyen de salariés</p>
                    <p>≤ 25</p>
                  </div>
                </div>
              }
            >
              <HelpCircle className="w-4 h-4 text-slate-400 cursor-pointer" />
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-6">
        {/* Titre avec icône */}
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded bg-pink-100 flex items-center justify-center">
            <ClipboardCheck className="w-4 h-4 text-pink-600" />
          </div>
          <h3 className="font-semibold text-sm text-slate-900  m-0">
            Mission complémentaire
          </h3>
        </div>

        {/* Déclarations missions */}
        <div className="flex flex-col gap-2 text-xs mb-4">
          <div className="flex justify-between gap-2">
            <span className="text-slate-500">Déclaration IR</span>
            <p>{overlayData.client.missionIR === "oui" ? "☒" : "☐"}</p>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-slate-500">Juridique AGO</span>
            <p>{overlayData.client.missionJuridique === "oui" ? "☒" : "☐"}</p>
          </div>

          {/* Commentaire */}
          <div className="mt-2">
            <span className="font-medium">Commentaire : </span>
            <span>
              {overlayData.suivi.missionComplementaire ||
                "Aucun commentaire pour l'instant"}
            </span>
          </div>
        </div>

        {/* Affectation du résultat */}
        <h4 className="font-semibold text-slate-900 mb-2 text-xs">
          Affectation du résultat
        </h4>
        <div className="flex flex-col gap-2 text-xs">
          <div className="flex justify-between items-center gap-2">
            <span className="text-slate-500">- Réserve légale</span>
            <p>{CheckboolS(overlayData.suivi.reserveLegale)}</p>
            <p>
              {overlayData.suivi.reserveLegaleMontant || "\u00A0\u00A0\u00A0"} €
            </p>
          </div>
          <div className="flex justify-between items-center gap-2">
            <span className="text-slate-500">
              Convention réglementées à approuver
            </span>
            <p>{CheckboolS(overlayData.suivi.dotee)}</p>
          </div>
          <div className="flex justify-between items-center gap-2 ml-4">
            <span className="text-slate-500">- Dividendes</span>
            <p>{CheckboolS(overlayData.suivi.dispense)}</p>
          </div>
          <div className="flex justify-between items-center gap-2 ml-4">
            <span className="text-slate-500">
              Si SARL, montant soumis à cotisations sociales
            </span>
            <p>{overlayData.suivi.sarl} €</p>
          </div>
          <div className="flex justify-between items-center gap-2 ml-4">
            <span className="text-slate-500">- Autres</span>
            <p>{overlayData.suivi.autres}</p>
          </div>
          <div className="flex justify-between items-center gap-2 ml-4">
            <span className="text-slate-500">- Report à nouveau</span>
            <p>{CheckboolS(overlayData.suivi.report)}</p>
          </div>
        </div>

        {/* Convention */}
        <h4 className="font-semibold text-slate-900 mt-4 mb-2 text-xs">
          Convention
        </h4>
        <div className="flex flex-col gap-2 text-xs">
          <div className="flex justify-between gap-2">
            <span className="text-slate-500">Rémunération à approuver</span>
            <p>{Checkbool(overlayData.suivi.remuneration)}</p>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-slate-500">
              Convention réglementées à approuver
            </span>
            <p>{Checkbool(overlayData.suivi.convention)}</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-6">
        <div className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-900 mb-3">
          <div className="w-2 h-2 rounded-full bg-[#8B1538]"></div>
          Événements cabinet
        </div>
        {overlayData.suivi.historiques &&
        overlayData.suivi.historiques.length > 0 ? (
          overlayData.suivi.historiques.map((histo) => (
            <div
              key={histo.id}
              style={{
                position: "relative",
                paddingLeft: "20px",
                marginBottom: "15px",
                borderLeft: "2px solid #cbd5e1",
              }}
            >
              {/* Pastille */}
              <div
                style={{
                  position: "absolute",
                  left: "-6px",
                  top: "0",
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  backgroundColor: "#2563eb",
                  border: "2px solid #fff",
                }}
              ></div>

              {/* Contenu */}
              <div
                style={{
                  background: "#eff6ff",
                  borderRadius: "8px",
                  padding: "10px",
                  border: "1px solid #cbd5e1",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "4px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#1e40af",
                    }}
                  >
                    {histo.titre}
                  </p>
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#1e40af",
                    }}
                  >
                    {formatDateWithoutYear(histo.createdAt)}
                  </span>
                </div>

                <p
                  style={{
                    fontSize: "13px",
                    color: "#1e3a8a",
                    marginBottom: "4px",
                  }}
                >
                  {histo.description}
                </p>
              </div>
            </div>
          ))
        ) : (
          <p style={{ fontStyle: "italic", color: "grey" }}>
            Aucun historique pour l'instant
          </p>
        )}
      </div>

      <br />
      <br />
      <br />
      <br />
    </div>
  );
};

export default OverlayDetails;
